/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { useTranslation } from 'react-i18next';
 

import {ReactComponent as BackgroundSprite1} from '../icons/color_sprite_bg_1.svg';
import {ReactComponent as BackgroundSprite2} from '../icons/color_sprite_bg_2.svg';

import {ReactComponent as IconFemale} from '../icons/female.svg';
import {ReactComponent as IconMale} from '../icons/male.svg';

import {ReactComponent as IconScroll0} from '../icons/scroll_0.svg';
import {ReactComponent as IconScroll1} from '../icons/scroll_1.svg';
import {ReactComponent as IconScroll2} from '../icons/scroll_2.svg';
import {ReactComponent as IconScroll3} from '../icons/scroll_3.svg';
import {ReactComponent as IconScroll4} from '../icons/scroll_4.svg';

import {ReactComponent as IconGo} from '../icons/arrow_go.svg';

import styles from '../styles/splashscreen.module.scss'
import LanguageSwitcherContainer from '../containers/LanguageSwitcherContainer';
import { LogoText } from './LogoText';
import { Gender, LibraryData, UIState } from '../store/interfaces';
import { useRef, useState } from 'react';
import { generateBackgroundSplashBaseFill, generateBackgroundSplashContrastFill, generateBackgroundStyle, generateGenderStroke } from '../helpers/helpers';
import { PreviewContainer, PreviewContainerModel } from './Preview';
import { isMobile } from 'react-device-detect';
import LinkFooterContainer from '../containers/LinkFooterContainer';
 
interface SplashscreenProps {
    ui: UIState;
    library: LibraryData;
    onUiGoTo: (route: string) => void;
    onUiSetCode: (code: string) => void;
    onUiStartNew: () => void;
    onGetConfig: () => void;    
}
  
export function Splashscreen(props: SplashscreenProps) {
    const {t} = useTranslation();

    const [activeInspirationIndex, setActiveInspirationIndex] = useState(0);

    const eventRef = useRef<HTMLDivElement>(null!);
    const leftRef = useRef<HTMLDivElement>(null!);
    const rightRef = useRef<HTMLDivElement>(null!);

    const inspiration = props.library.inspirations[activeInspirationIndex];

    const selectedBaseColor = props.library.baseColors.find((value) => {return value.id === inspiration.baseColorId;});
    const selectedContrastColor = props.library.contrastColors.find((value) => {return value.id === inspiration.contrastColorId;});

    const previewProduct1 = props.library.products.find((product) => {return product.article === inspiration.article1});
    const previewProduct2 = props.library.products.find((product) => {return product.article === inspiration.article2});

    return (
        <div className={styles.background}>
            {
                props.library.baseColors.map((color, index) => {
                    return <div key={index} className={styles.background} style={{background: generateBackgroundStyle(color.bgColor, true), opacity: color.id === selectedBaseColor?.id ? 1.0 : 0.0}}></div>
                })
            }
            <div className={styles.container} ref={eventRef}>
                <div className={styles.genderInfo}>
                    {inspiration.gender === Gender.Female && <IconFemale stroke={generateGenderStroke(selectedBaseColor)}/>}
                    {inspiration.gender === Gender.Male && <IconMale stroke={generateGenderStroke(selectedBaseColor)}/>}
                </div>
                <div className={styles.logoContainer}>
                    <LogoText size={'big'} center={true} />
                </div>
                {/*<div className={styles.switcherContainer}>
                    <button className={`${styles.buttonSub} ${styles.button} ${styles.buttonWhite}`}>E.S. Motion 2020</button>
                </div>*/}               
                <div className={`${styles.previewContainer} ${styles.previewContainerLeft}`} ref={leftRef}>
                    <BackgroundSprite1 className={`${styles.backgroundSplash} ${styles.backgroundSplash1}`} fill={generateBackgroundSplashContrastFill(selectedContrastColor)} />
                </div>
                <div className={`${styles.centerImageContainer}`}>
                    <div>
                        <img alt="Example basic t-shirt 1" src={inspiration.centerImage1} />
                    </div>
                    <div>
                        <img alt="Example basic t-shirt 2" src={inspiration.centerImage2} />
                    </div>
                </div>
                <button data-testid="mymotion_clickScroll" className={styles.inspirationSwitch} onClick={() => {
                    var activeIndex = activeInspirationIndex + 1;
                    if (activeIndex >= props.library.inspirations.length) {
                        activeIndex = 0;
                    }
                    setActiveInspirationIndex(activeIndex);
                }}>
                    {activeInspirationIndex === 0 && <IconScroll0/>}
                    {activeInspirationIndex === 1 && <IconScroll1/>}
                    {activeInspirationIndex === 2 && <IconScroll2/>}
                    {activeInspirationIndex === 3 && <IconScroll3/>}
                    {activeInspirationIndex === 4 && <IconScroll4/>}
                </button>
                <div className={`${styles.previewContainer} ${styles.previewContainerRight}`} ref={rightRef}>
                    <BackgroundSprite2 className={`${styles.backgroundSplash} ${styles.backgroundSplash2}`} fill={generateBackgroundSplashBaseFill(selectedBaseColor)} />
                </div>
                <div className={styles.startContainer}>
                    <button data-testid="mymotion_start" className={`${styles.buttonPrimary} ${styles.buttonBig} ${styles.buttonStart}`} onClick={() => {props.onUiStartNew()}}>{t('splashscreen.link')} <IconGo/></button>
                </div>
                <div className={styles.codeContainer}>
                    <p dangerouslySetInnerHTML={{__html: t('splashscreen.code.info')}}></p>

                    <div className={styles.code}>
                        <input type="text" value={props.ui.code} disabled={props.ui.isLoadingConfig} onChange={(evt) => {props.onUiSetCode(evt.target.value)}} placeholder={t('splashscreen.code.placeholder') as string}/>
                        <button data-testid="mymotion_start_savedDesign" className={`${styles.buttonSub} ${styles.button} ${styles.codeButton}`} disabled={props.ui.isLoadingConfig} onClick={() => {props.onGetConfig()}}>{t('splashscreen.code.continue')}</button>
                    </div>
                    {props.ui.loadingConfigError !== null && <p className={styles.error}>{t(props.ui.loadingConfigError)}</p>}

                </div>
                <PreviewContainer
                    className={styles.fullCanvas}
                    eventRef={eventRef}
                >
                    <PreviewContainerModel divRef={leftRef} library={props.library} disableZoom={isMobile} model={inspiration.article1} modelData={previewProduct1?.modelData} xOffset={0} baseColorId={inspiration.baseColorId} contrastColorId={inspiration.contrastColorId} straussLogo={inspiration.straussLogo} showPlanes={false} showSplashLogo={true}/>
                    <PreviewContainerModel divRef={rightRef} library={props.library} disableZoom={isMobile} model={inspiration.article2} modelData={previewProduct2?.modelData} xOffset={0} baseColorId={inspiration.baseColorId} contrastColorId={inspiration.contrastColorId} straussLogo={inspiration.straussLogo} showPlanes={false} isRight={true} showSplashLogo={true}/>
                </PreviewContainer>
            </div>
            <div className={styles.footer}><LanguageSwitcherContainer/><span className={styles.footerSeparator}></span><LinkFooterContainer fullWidth={true}/></div>
        </div>
    );
};
 